import type { IEventItemProps } from 'src/types/product';

import { fDate, fIsSame } from 'src/utils/format-time';

import {
  ALL_DAY_ACTIVITY_TIME,
  PLACEHOLDER_ACTIVITY_DATE_ISO,
  PLACEHOLDER_ACTIVITY_START_TIME,
} from 'src/constants/activity';

import { getTime } from './constants';

type Schedule = IEventItemProps['schedule'];

export const hasDisplayableActivityDate = (schedule?: Schedule) =>
  Boolean(getActivityDateLabel(schedule));

export const hasDisplayableActivityTime = (schedule?: Schedule) =>
  Boolean(getActivityTimeLabel(schedule));

export const getActivityDateLabel = (schedule?: Schedule): string => {
  if (!schedule?.startDate || schedule.startDate === PLACEHOLDER_ACTIVITY_DATE_ISO) {
    return 'Tarih Sor';
  }

  if (!schedule.endDate || fIsSame(schedule.startDate, schedule.endDate)) {
    return fDate(schedule.startDate);
  }

  return `${fDate(schedule.startDate)}-${fDate(schedule.endDate)}`;
};

const isValidNumber = (value: unknown): value is number => typeof value === 'number';

export const getActivityTimeLabel = (schedule?: Schedule): string => {
  const firstDay = schedule?.days?.[0];

  if (!firstDay) {
    return '';
  }

  if (firstDay.startTime === ALL_DAY_ACTIVITY_TIME) {
    return getTime(firstDay.startTime) ?? '24 saat açık';
  }

  const hasStart =
    isValidNumber(firstDay.startTime) && firstDay.startTime !== PLACEHOLDER_ACTIVITY_START_TIME;

  if (isValidNumber(firstDay.startTime) && firstDay.startTime === PLACEHOLDER_ACTIVITY_START_TIME) {
    return 'Saat Sor';
  }

  const startText = hasStart ? (getTime(firstDay.startTime) ?? '') : '';

  return startText;
};

export const getActivityDateTimeLabel = (schedule?: Schedule): string =>
  [getActivityDateLabel(schedule), getActivityTimeLabel(schedule)].filter(Boolean).join(' - ');

// ----------------------------------------------------------------------
// Date Filtering Utilities
// ----------------------------------------------------------------------

/**
 * Bir tarihin bugün veya gelecekte olup olmadığını kontrol eder
 * @param dateValue - Kontrol edilecek tarih (ISO string veya Date)
 * @param fallbackDateValue - dateValue yoksa kullanılacak fallback tarih
 * @returns true eğer tarih bugün veya gelecekte ise
 */
export const isFutureOrToday = (
  dateValue?: string | Date,
  fallbackDateValue?: string | Date
): boolean => {
  const value = dateValue ?? fallbackDateValue;

  if (!value) {
    return true; // Tarih yoksa göster
  }

  const parsedDate = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(parsedDate.getTime())) {
    return true; // Geçersiz tarih ise göster
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);

  return parsedDate >= today;
};

/**
 * Bir aktivitenin en erken tarihini bulur (schedule.days veya schedule.startDate'den)
 * @param activity - Aktivite objesi
 * @returns En erken tarih veya null
 */
export const getEarliestActivityDate = (activity: any): Date | null => {
  if (!activity?.schedule) {
    return null;
  }

  let earliestDate: Date | null = null;

  // schedule.days array'indeki en erken tarihi bul
  if (activity.schedule.days && activity.schedule.days.length > 0) {
    const dates = activity.schedule.days
      .map((day: any) => {
        // day.date varsa onu kullan
        if (day.date) {
          const date = new Date(day.date);
          return !Number.isNaN(date.getTime()) ? date : null;
        }
        return null;
      })
      .filter((date: Date | null) => date !== null);

    if (dates.length > 0) {
      earliestDate = new Date(Math.min(...dates.map((d: Date) => d.getTime())));
    }
  }

  // Eğer days array'inde tarih yoksa, startDate'i kullan
  if (!earliestDate && activity.schedule.startDate) {
    const startDate = new Date(activity.schedule.startDate);
    // Placeholder tarihleri kontrol et
    if (
      !Number.isNaN(startDate.getTime()) &&
      activity.schedule.startDate !== PLACEHOLDER_ACTIVITY_DATE_ISO &&
      activity.schedule.startDate !== '1970-01-01T00:00:00.000Z'
    ) {
      earliestDate = startDate;
    }
  }

  return earliestDate;
};

/**
 * Bir aktivitenin gelecek tarihli günlerinden birini bulur
 * @param activity - Aktivite objesi
 * @param today - Bugünün tarihi (opsiyonel, default: şu anki tarih)
 * @returns Gelecek tarihli bir gün veya null
 */
export const getFutureActivityDate = (activity: any, today?: Date): Date | null => {
  if (!activity?.schedule) {
    return null;
  }

  const todayDate = today || new Date();
  todayDate.setHours(0, 0, 0, 0);

  // schedule.days array'indeki gelecek tarihli günleri bul
  if (activity.schedule.days && activity.schedule.days.length > 0) {
    const futureDates = activity.schedule.days
      .map((day: any) => {
        if (day.date) {
          const date = new Date(day.date);
          if (!Number.isNaN(date.getTime())) {
            date.setHours(0, 0, 0, 0);
            // Bugün veya gelecek tarihli günleri bul
            if (date >= todayDate) {
              return date;
            }
          }
        }
        return null;
      })
      .filter((date: Date | null) => date !== null);

    if (futureDates.length > 0) {
      // En erken gelecek tarihi döndür
      return new Date(Math.min(...futureDates.map((d: Date) => d.getTime())));
    }
  }

  // Eğer days array'inde gelecek tarih yoksa, startDate'i kontrol et
  if (activity.schedule.startDate) {
    const startDate = new Date(activity.schedule.startDate);
    if (
      !Number.isNaN(startDate.getTime()) &&
      activity.schedule.startDate !== PLACEHOLDER_ACTIVITY_DATE_ISO &&
      activity.schedule.startDate !== '1970-01-01T00:00:00.000Z'
    ) {
      startDate.setHours(0, 0, 0, 0);
      if (startDate >= todayDate) {
        return startDate;
      }
    }
  }

  return null;
};

/**
 * Aktivite listesinden geçmiş tarihli aktiviteleri filtreler
 * @param activities - Aktivite array'i
 * @param options - Filtreleme seçenekleri
 * @returns Filtrelenmiş aktivite array'i
 */
export const filterPastActivities = (
  activities: any[],
  options: {
    includeToday?: boolean; // Bugünü dahil et (default: true)
    includePlaceholderDates?: boolean; // Placeholder tarihleri dahil et (default: true)
  } = {}
): any[] => {
  const { includeToday = true, includePlaceholderDates = true } = options;

  if (!activities || !Array.isArray(activities) || activities.length === 0) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return activities.filter((activity: any) => {
    if (!activity?.schedule) {
      return includePlaceholderDates; // Schedule yoksa placeholder olarak kabul et
    }

    // Placeholder tarih kontrolü
    if (
      includePlaceholderDates &&
      (activity.schedule.startDate === PLACEHOLDER_ACTIVITY_DATE_ISO ||
        activity.schedule.startDate === '1970-01-01T00:00:00.000Z')
    ) {
      return true;
    }

    // Aktivitenin gelecek tarihli bir günü var mı kontrol et
    // Eğer aktivite birden fazla gün içeriyorsa, en az bir gelecek tarihli gün varsa göster
    const futureDate = getFutureActivityDate(activity, today);

    // Gelecek tarihli gün varsa göster
    if (futureDate) {
      return true;
    }

    // Gelecek tarihli gün yoksa, en erken tarihi kontrol et (fallback)
    const earliestDate = getEarliestActivityDate(activity);

    // Tarih yoksa göster (placeholder olarak kabul et)
    if (!earliestDate) {
      return includePlaceholderDates;
    }

    // Tarihi sadece gün bazında karşılaştır (saat bilgisini yok say)
    const activityDate = new Date(earliestDate);
    activityDate.setHours(0, 0, 0, 0);

    // Bugün ve gelecek tarihleri göster
    if (includeToday) {
      return activityDate >= today;
    }

    // Sadece gelecek tarihleri göster (bugünü dahil etme)
    return activityDate > today;
  });
};
